/**
 * ProductMediaDeleteDialog
 * ───────────────────────────────────────────────────────────────────────────
 * The delete confirmation for a product's SAVED photos: the cover and the
 * extra angles, across every catalogue (restaurant, healthcare,
 * retailer/wholesaler, brand). Tick any number, confirm once, and they are
 * removed from the product and from Cloudinary right away through
 * apiRemoveProductImages, without saving the rest of the form.
 *
 * Only https URLs are offered. A dataURL is a photo that has been picked but
 * never uploaded; the forms discard those locally, as before.
 *
 * On catalogues whose model requires a cover (everything but restaurant
 * dishes) the cover is not offered at all: the server would refuse it with
 * COVER_REQUIRED, so the dialog lists only the extra angles there and the
 * cover keeps its Replace control. Mirrors coverIsRemovable() on the server.
 *
 *   const [photoDelete, setPhotoDelete] = useState(null); // url clicked
 *   <ProductMediaDeleteDialog
 *     target={photoDelete}
 *     productType="restaurant"
 *     productId={productId}
 *     productName={product.name}
 *     image={product.image}
 *     additionalImages={product.additionalImages}
 *     onClose={() => setPhotoDelete(null)}
 *     onRemoved={({ image, additionalImages }) => setProduct((p) => ({ ...p, image, additionalImages }))}
 *   />
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import MediaDeleteDialog from "./MediaDeleteDialog";
import { apiRemoveProductImages } from "../api";

const isSaved = (url) => typeof url === "string" && /^https?:\/\//i.test(url);

/** Only restaurant dishes may exist without a cover (photos arrive after a
    menu import); every other catalogue declares `image` required. */
export const coverIsRemovable = (productType) => productType === "restaurant";

/** The saved photos on a product, shaped for MediaDeleteDialog. */
export const listProductPhotos = (image, additionalImages, { includeCover = true } = {}) => {
  const items = [];
  if (includeCover && isSaved(image)) {
    items.push({ key: image, url: image, label: "Cover photo", aspect: "1 / 1" });
  }
  (Array.isArray(additionalImages) ? additionalImages : []).forEach((url, i) => {
    if (isSaved(url)) {
      items.push({ key: url, url, label: `Extra angle ${i + 1}`, aspect: "1 / 1" });
    }
  });
  return items;
};

const ProductMediaDeleteDialog = ({
  /** The URL whose delete control was clicked, or null when closed. */
  target,
  productType,
  productId,
  productName,
  image,
  additionalImages,
  /** Brand products are addressed through their brand. */
  brandId,
  onClose,
  onRemoved,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retryKeys, setRetryKeys] = useState(null);

  const items = useMemo(
    () => listProductPhotos(image, additionalImages, { includeCover: coverIsRemovable(productType) }),
    [image, additionalImages, productType]
  );
  const show = !!target && items.length > 0;
  const initialKeys = retryKeys || (target && items.some((it) => it.key === target) ? [target] : []);

  const close = () => {
    if (busy) return;
    setError("");
    setRetryKeys(null);
    if (onClose) onClose();
  };

  const handleConfirm = async (urls) => {
    setBusy(true);
    setError("");
    try {
      const res = await apiRemoveProductImages(productType, productId, urls, { brandId });
      const body = res && res.data ? res.data : {};
      const removed = body.removed || [];
      const failed = body.failed || [];
      const next = body.data || {};
      if (removed.length && onRemoved) {
        onRemoved({
          image: typeof next.image === "string" ? next.image : image,
          additionalImages: Array.isArray(next.additionalImages) ? next.additionalImages : additionalImages,
        });
      }
      if (removed.length) toast.success(body.message || "Photos removed.");
      if (failed.length) {
        setError(body.message || "Some photos could not be removed. Try again.");
        setRetryKeys(failed.map((f) => f.url));
        return;
      }
      setRetryKeys(null);
      if (onClose) onClose();
    } catch (err) {
      const body = err && err.response && err.response.data;
      // A 502 still carries what did and did not go; a 400 (COVER_REQUIRED)
      // carries only the reason. Either way the operator keeps the dialog.
      if (body && Array.isArray(body.removed) && body.removed.length && onRemoved) {
        onRemoved({
          image: body.data && typeof body.data.image === "string" ? body.data.image : image,
          additionalImages:
            body.data && Array.isArray(body.data.additionalImages)
              ? body.data.additionalImages
              : additionalImages,
        });
      }
      setError((body && body.message) || err.message || "Could not remove photos.");
      if (body && Array.isArray(body.failed) && body.failed.length) {
        setRetryKeys(body.failed.map((f) => f.url));
      }
    } finally {
      setBusy(false);
    }
  };

  const single = items.length === 1;
  return (
    <MediaDeleteDialog
      show={show}
      title={
        single
          ? `Delete the ${items[0].label.toLowerCase()}?`
          : `Delete photos${productName ? ` from ${productName}` : ""}?`
      }
      intro={
        single
          ? "This permanently removes it from the product and from Cloudinary."
          : "Tick the photos to remove. They are deleted from the product and from Cloudinary now; the rest of the form is not saved."
      }
      items={items}
      initialKeys={initialKeys}
      noun="photo"
      busy={busy}
      error={error}
      onConfirm={handleConfirm}
      onCancel={close}
    />
  );
};

export default ProductMediaDeleteDialog;
